import React, { useState, useEffect, useCallback } from 'react';
import {
	Alert,
	Box,
	Button,
	Container,
	Paper,
	Snackbar,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	InputAdornment,
	Typography,
	useTheme,
	useMediaQuery,
	Stack,
} from '@mui/material';
import {
	Add as AddIcon,
	Delete as DeleteIcon,
	DragIndicator as DragIndicatorIcon,
	Edit as EditIcon,
	ArrowUpward as ArrowUpwardIcon,
	ArrowDownward as ArrowDownwardIcon,
	Search as SearchIcon,
} from '@mui/icons-material';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axios from 'axios';
import { useNavigate, useSearchParams as useUrlSearchParams } from 'react-router-dom';
import i18next from '../../lang';
import { adminStyles } from '../../theme/adminStyles';
import MobileCardView, { MobileCardField } from '../../components/MobileCardView';

interface ProductSummary {
	id: number;
	title: string;
	price: number;
}

interface ProductSetWithProducts {
	id: number;
	name: string;
	description: string;
	price: number;
	sortOrder: number;
	products: ProductSummary[];
}

// ドラッグ＆ドロップ対応テーブル行
const SortableProductSetRow: React.FC<{
	productSet: ProductSetWithProducts;
	onEdit: (id: number) => void;
	onDelete: (id: number) => void;
}> = ({ productSet, onEdit, onDelete }) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: productSet.id });
	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};
	return (
		<TableRow ref={setNodeRef} style={style} sx={{ '&:hover': { bgcolor: '#f8f9fa' }, '& .MuiTableCell-root': { py: '8px' } }}>
			<TableCell sx={{ width: 30, p: 0, textAlign: 'center', cursor: 'grab' }} {...attributes} {...listeners}>
				<DragIndicatorIcon sx={{ fontSize: '1rem', color: 'text.secondary', verticalAlign: 'middle' }} />
			</TableCell>
			<TableCell>{productSet.id}</TableCell>
			<TableCell>{productSet.name}</TableCell>
			<TableCell>{productSet.description}</TableCell>
			<TableCell>
				{productSet.price
					? i18next.t('common.price_yen', { price: productSet.price.toLocaleString() })
					: "-"
				}
			</TableCell>
			<TableCell>{productSet.products.length}</TableCell>
			<TableCell>
				<Button size="small" startIcon={<EditIcon />} onClick={() => onEdit(productSet.id)}>
					{i18next.t('common.edit')}
				</Button>
				<Button size="small" startIcon={<DeleteIcon />} color="error" onClick={() => onDelete(productSet.id)}>
					{i18next.t('common.delete')}
				</Button>
			</TableCell>
		</TableRow>
	);
};

const ProductSetManagement: React.FC = () => {
	const navigate = useNavigate();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const [productSets, setProductSets] = useState<ProductSetWithProducts[]>([]);

	// URL クエリと同期 (戻るボタンで検索状態を復元するため)
	const [urlParams, setUrlParams] = useUrlSearchParams();
	const [searchKeyword, setSearchKeyword] = useState(urlParams.get('keyword') || '');
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
	const [sortConfig, setSortConfig] = useState<{
		key: keyof ProductSetWithProducts | 'productsLength' | null;
		direction: 'asc' | 'desc';
	}>({
		key: (urlParams.get('sort') as any) || null,
		direction: (urlParams.get('dir') === 'desc' ? 'desc' : 'asc') as 'asc' | 'desc'
	});

	const syncToUrl = useCallback((overrides?: {
		keyword?: string;
		sortKey?: string | null;
		sortDir?: 'asc' | 'desc';
	}) => {
		const next = new URLSearchParams();
		const keyword = overrides?.keyword !== undefined ? overrides.keyword : searchKeyword;
		const sKey = overrides?.sortKey !== undefined ? overrides.sortKey : sortConfig.key;
		const sDir = overrides?.sortDir !== undefined ? overrides.sortDir : sortConfig.direction;

		if (keyword) next.set('keyword', keyword);
		if (sKey) next.set('sort', String(sKey));
		if (sKey && sDir !== 'asc') next.set('dir', sDir);

		setUrlParams(next, { replace: true });
	}, [searchKeyword, sortConfig.key, sortConfig.direction, setUrlParams]);


	useEffect(() => {
		fetchProductSets();
	}, []);

	const fetchProductSets = async () => {
		try {
			const response = await axios.get('/api/v1/admin/product-sets');
			setProductSets(response.data);
		} catch (error) {
			console.error('Failed to fetch product sets:', error);
		}
	};

	const handleDelete = async (id: number) => {
		if (window.confirm(i18next.t('product_set.confirm_delete'))) {
			try {
				await axios.delete(`/api/v1/admin/product-sets/${id}`);
				fetchProductSets();
			} catch (error) {
				console.error('Failed to delete product set:', error);
			}
		}
	};

	const handleSort = (key: keyof ProductSetWithProducts | 'productsLength') => {
		const newDir: 'asc' | 'desc' = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
		setSortConfig({ key, direction: newDir });
		syncToUrl({ sortKey: String(key), sortDir: newDir });
	};

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
		useSensor(KeyboardSensor),
	);

	const filteredProductSets = React.useMemo(() => {
		if (!searchKeyword.trim()) return productSets;
		const keyword = searchKeyword.toLowerCase();
		return productSets.filter((ps) =>
			String(ps.id).includes(keyword) ||
			ps.name?.toLowerCase().includes(keyword) ||
			ps.description?.toLowerCase().includes(keyword)
		);
	}, [productSets, searchKeyword]);

	const sortedProductSets = React.useMemo(() => {
		if (!sortConfig.key) return [...filteredProductSets].sort((a, b) => (a.sortOrder ?? a.id) - (b.sortOrder ?? b.id));

		return [...filteredProductSets].sort((a, b) => {
			if (sortConfig.key === 'productsLength') {
				const aValue = a.products.length;
				const bValue = b.products.length;
				return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
			}

			const aValue = a[sortConfig.key];
			const bValue = b[sortConfig.key];

			if (typeof aValue === 'number' && typeof bValue === 'number') {
				return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
			}

			if (typeof aValue === 'string' && typeof bValue === 'string') {
				return sortConfig.direction === 'asc'
					? aValue.localeCompare(bValue)
					: bValue.localeCompare(aValue);
			}

			return 0;
		});
	}, [filteredProductSets, sortConfig]);

	const handleDragEnd = useCallback((event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;

		const oldIndex = sortedProductSets.findIndex(ps => ps.id === active.id);
		const newIndex = sortedProductSets.findIndex(ps => ps.id === over.id);
		if (oldIndex === -1 || newIndex === -1) return;

		const reordered = arrayMove(sortedProductSets, oldIndex, newIndex);
		const updates: { id: number; sortOrder: number }[] = [];
		const sortOrderBase = Math.min(...reordered.map(ps => ps.sortOrder || ps.id));
		const newProductSets = [...productSets];
		reordered.forEach((ps, idx) => {
			const newSortOrder = sortOrderBase + idx;
			const globalIdx = newProductSets.findIndex(p => p.id === ps.id);
			if (globalIdx !== -1) {
				newProductSets[globalIdx] = { ...newProductSets[globalIdx], sortOrder: newSortOrder };
			}
			updates.push({ id: ps.id, sortOrder: newSortOrder });
		});
		setProductSets(newProductSets);

		axios.put('/api/v1/admin/product-sets/sort-order', updates)
			.then(() => setSnackbar({ open: true, message: i18next.t('product.sort_order_updated'), severity: 'success' }))
			.catch(() => {
				setSnackbar({ open: true, message: i18next.t('product.sort_order_update_failed'), severity: 'error' });
				fetchProductSets();
			});
	}, [sortedProductSets, productSets]);

	const renderSortIcon = (key: keyof ProductSetWithProducts | 'productsLength') => {
		if (sortConfig.key !== key) return null;

		return sortConfig.direction === 'asc' ?
			<ArrowUpwardIcon sx={{ fontSize: '1rem', ml: 1, verticalAlign: 'middle' }} /> :
			<ArrowDownwardIcon sx={{ fontSize: '1rem', ml: 1, verticalAlign: 'middle' }} />;
	};

	const renderSortableHeader = (key: keyof ProductSetWithProducts | 'productsLength', label: string) => (
		<Box
			component="span"
			sx={{
				cursor: 'pointer',
				display: 'inline-flex',
				alignItems: 'center',
				'&:hover': { opacity: 0.7 }
			}}
			onClick={() => handleSort(key)}
		>
			{label}
			{renderSortIcon(key)}
		</Box>
	);

	const mobileCardFields: MobileCardField<ProductSetWithProducts>[] = [
		{ key: 'name', label: i18next.t('product_set.set_name'), primary: true },
		{ key: 'description', label: i18next.t('common.description') },
		{
			key: 'price', label: i18next.t('product.price'),
			getValue: (row) => row.price ? i18next.t('common.price_yen', { price: row.price.toLocaleString() }) : "-"
		},
		{ key: 'productsLength', label: i18next.t('product_set.product_count'), getValue: (row) => row.products.length },
		{
			key: 'actions', label: '', hideLabel: true,
			render: (row) => (
				<Stack direction="row" spacing={1}>
					<Button size="small" variant="outlined" startIcon={<EditIcon />}
						onClick={(e) => { e.stopPropagation(); navigate(`/admin/product-sets/edit/${row.id}`); }}>
						{i18next.t('common.edit')}
					</Button>
					<Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />}
						onClick={(e) => { e.stopPropagation(); handleDelete(row.id); }}>
						{i18next.t('common.delete')}
					</Button>
				</Stack>
			)
		}
	];

	return (
		<Container maxWidth={false} sx={adminStyles.fullWidthPageContainer}>
			{/* アクションボタン */}
			<Box sx={adminStyles.headerBox}>
				<Button
					variant="contained"
					startIcon={<AddIcon />}
					onClick={() => navigate('/admin/product-sets/create')}
					sx={adminStyles.actionButton}
				>
					{i18next.t('auth.new_registration')}
				</Button>
			</Box>

			{/* 検索 */}
			<Box sx={{ mb: 2 }}>
				<TextField
					size="small"
					placeholder={i18next.t('product_set.search_placeholder')}
					value={searchKeyword}
					onChange={(e) => setSearchKeyword(e.target.value)}
					onBlur={() => syncToUrl()}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon />
							</InputAdornment>
						),
					}}
					sx={{ width: { xs: '100%', sm: '360px' } }}
				/>
			</Box>

			{/* セット商品リスト */}
			<Paper sx={adminStyles.tablePaper}>
				{isMobile ? (
					<MobileCardView
						data={sortedProductSets}
						fields={mobileCardFields}
						keyExtractor={(row) => row.id}
					/>
				) : (
					<TableContainer>
						<Table>
							<TableHead>
								<TableRow sx={adminStyles.tableHeaderRow}>
									<TableCell sx={{ ...adminStyles.tableHeaderCell as any, width: 30, p: 0 }} />
									<TableCell sx={adminStyles.tableHeaderCell}>{renderSortableHeader('id', i18next.t('common.id'))}</TableCell>
									<TableCell sx={adminStyles.tableHeaderCell}>{renderSortableHeader('name', i18next.t('product_set.set_name'))}</TableCell>
									<TableCell sx={adminStyles.tableHeaderCell}>{renderSortableHeader('description', i18next.t('common.description'))}</TableCell>
									<TableCell sx={adminStyles.tableHeaderCell}>{renderSortableHeader('price', i18next.t('product.price'))}</TableCell>
									<TableCell sx={adminStyles.tableHeaderCell}>{renderSortableHeader('productsLength', i18next.t('product_set.product_count'))}</TableCell>
									<TableCell sx={adminStyles.tableHeaderCell}>{i18next.t('common.actions')}</TableCell>
								</TableRow>
							</TableHead>
							<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
								<SortableContext items={sortedProductSets.map(ps => ps.id)} strategy={verticalListSortingStrategy}>
									<TableBody>
										{sortedProductSets.map((productSet) => (
											<SortableProductSetRow
												key={productSet.id}
												productSet={productSet}
												onEdit={(id) => navigate(`/admin/product-sets/edit/${id}`)}
												onDelete={handleDelete}
											/>
										))}
									</TableBody>
								</SortableContext>
							</DndContext>
						</Table>
					</TableContainer>
				)}
			</Paper>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={3000}
				onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert
					onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
					severity={snackbar.severity}
					variant="filled"
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default ProductSetManagement;
